import { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { useToast } from "../contexts/ToastContext";

// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────
const TABS = [
  { id: "langues",   label: "Langues & Traductions", icon: "🌍" },
  { id: "devises",   label: "Devises & Taux",         icon: "💱" },
  { id: "mentions",  label: "Mentions légales",        icon: "⚖️" },
  { id: "export",    label: "Documents export",        icon: "📦" },
];

const DOC_TYPES = [
  { value: "facture",       label: "Facture" },
  { value: "avoir",         label: "Avoir" },
  { value: "devis",         label: "Devis" },
  { value: "bon_commande",  label: "Bon de commande" },
  { value: "bon_livraison", label: "Bon de livraison" },
  { value: "proforma",      label: "Pro Forma" },
  { value: "relance",       label: "Relance" },
];

const MENTION_TYPES = [
  { value: "tva",                 label: "TVA / Numéro fiscal" },
  { value: "pénalités_retard",    label: "Pénalités de retard" },
  { value: "escompte",            label: "Escompte" },
  { value: "tribunal_compétent",  label: "Tribunal compétent" },
  { value: "rgpd",                label: "RGPD" },
  { value: "autre",               label: "Autre" },
];

const INCOTERMS = ["EXW","FCA","FAS","FOB","CFR","CIF","CPT","CIP","DAP","DPU","DDP"];

// ─────────────────────────────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────────────────────────────
const card = { background: "#ffffff", border: "1px solid #21262d", borderRadius: 10, padding: "18px 20px", marginBottom: 14 };
const inp  = { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "7px 10px", color: "#111827", fontSize: 13, width: "100%" };
const lbl  = { fontSize: 11, color: "#4b5563", display: "block", marginBottom: 3 };
const btn  = (color = "#1e40af") => ({ background: color, border: "none", borderRadius: 6, padding: "7px 16px", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" });
const outBtn = { background: "transparent", border: "1px solid #e5e7eb", borderRadius: 6, padding: "7px 14px", color: "#4b5563", fontSize: 13, cursor: "pointer" };

function Badge({ color, children }) {
  return <span style={{ background: color + "22", color, borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 600 }}>{children}</span>;
}

// ─────────────────────────────────────────────────────────────────
// LANGUES TAB
// ─────────────────────────────────────────────────────────────────
function LanguesTab() {
  const { showToast } = useToast();
  const [languages, setLanguages]   = useState([]);
  const [fieldKeys, setFieldKeys]   = useState([]);
  const [docType, setDocType]       = useState("facture");
  const [selLang, setSelLang]       = useState(null);
  const [translations, setTrans]    = useState({});
  const [pendingTr, setPendingTr]   = useState({});
  const [autoResult, setAutoResult] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [aiLoading, setAiLoading]   = useState(false);
  const [newLang, setNewLang]       = useState({ code: "", name: "", flag: "" });
  const [showAdd, setShowAdd]       = useState(false);
  const [saving, setSaving]         = useState(false);

  const loadAll = useCallback(() => {
    Promise.all([
      api.multilingues.languages(),
      api.multilingues.fieldKeys(),
    ]).then(([langs, keys]) => {
      setLanguages(langs);
      setFieldKeys(keys);
    });
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadTranslations = useCallback((type) => {
    setLoading(true);
    api.multilingues.translations({ document_type: type })
      .then(rows => {
        const map = {};
        for (const r of rows) {
          if (!map[r.field_key]) map[r.field_key] = {};
          map[r.field_key][r.language_code] = r.field_value;
        }
        setTrans(map);
        setPendingTr({});
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadTranslations(docType); }, [docType, loadTranslations]);

  function updateCell(key, lang, value) {
    setPendingTr(p => ({ ...p, [`${key}__${lang}`]: value }));
    setTrans(p => ({ ...p, [key]: { ...(p[key] || {}), [lang]: value } }));
  }

  async function saveAll() {
    const rows = [];
    for (const fk of fieldKeys) {
      for (const lang of languages) {
        const val = translations[fk.key]?.[lang.code];
        if (val !== undefined) rows.push({ document_type: docType, language_code: lang.code, field_key: fk.key, field_value: val });
      }
    }
    setSaving(true);
    try {
      await api.multilingues.saveBatch({ translations: rows });
      showToast("Traductions enregistrées.", "success");
      setPendingTr({});
    } catch (err) {
      showToast(err.message, "error");
    } finally { setSaving(false); }
  }

  async function autoTranslate() {
    const activeLangs = languages.filter(l => l.is_active && l.code !== 'fr').map(l => l.code);
    const missingKeys = fieldKeys.map(f => f.key).filter(k => !translations[k]?.['fr']);
    if (activeLangs.length === 0) { showToast("Aucune langue cible active.", "warning"); return; }
    setAiLoading(true);
    try {
      const res = await api.multilingues.autoTranslate({
        document_type: docType, source_lang: 'fr',
        target_langs: activeLangs,
        fields: fieldKeys.map(f => f.key),
      });
      if (res.error) { showToast(res.error, "error"); return; }
      setAutoResult(res.translations || []);
      showToast(`${res.count || 0} traductions générées — vérifiez avant de sauvegarder.`, "success");
    } catch (err) {
      showToast(err.message, "error");
    } finally { setAiLoading(false); }
  }

  function applyAutoResult() {
    if (!autoResult) return;
    const updated = { ...translations };
    for (const t of autoResult) {
      if (!updated[t.key]) updated[t.key] = {};
      updated[t.key][t.lang] = t.value;
    }
    setTrans(updated);
    setAutoResult(null);
    showToast("Traductions appliquées. Cliquez « Sauvegarder » pour confirmer.", "success");
  }

  async function toggleActive(lang) {
    try {
      await api.multilingues.updateLanguage(lang.code, { is_active: !lang.is_active });
      loadAll();
    } catch (err) { showToast(err.message, "error"); }
  }

  async function setDefault(lang) {
    try {
      await api.multilingues.updateLanguage(lang.code, { is_default: true });
      loadAll(); showToast(`${lang.name} défini comme langue par défaut.`, "success");
    } catch (err) { showToast(err.message, "error"); }
  }

  async function addLanguage(e) {
    e.preventDefault();
    try {
      await api.multilingues.createLanguage(newLang);
      loadAll(); setShowAdd(false); setNewLang({ code: "", name: "", flag: "" });
      showToast("Langue ajoutée.", "success");
    } catch (err) { showToast(err.message, "error"); }
  }

  async function deleteLang(lang) {
    if (!window.confirm(`Supprimer la langue "${lang.name}" et toutes ses traductions ?`)) return;
    try { await api.multilingues.deleteLanguage(lang.code); loadAll(); showToast("Langue supprimée.", "success"); }
    catch (err) { showToast(err.message, "error"); }
  }

  const activeLangs = languages.filter(l => l.is_active);
  const pendingCount = Object.keys(pendingTr).length;

  return (
    <div>
      {/* Language management */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, color: "#111827", fontSize: 15 }}>Langues disponibles</h3>
          <button onClick={() => setShowAdd(!showAdd)} style={btn()}>+ Ajouter</button>
        </div>

        {showAdd && (
          <form onSubmit={addLanguage} style={{ display: "flex", gap: 10, marginBottom: 14, background: "#ffffff", padding: 12, borderRadius: 8 }}>
            <div style={{ flex: "0 0 80px" }}>
              <label style={lbl}>Code *</label>
              <input style={inp} placeholder="ja" maxLength={5} value={newLang.code} onChange={e => setNewLang(p => ({ ...p, code: e.target.value }))} required />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Nom *</label>
              <input style={inp} placeholder="日本語" value={newLang.name} onChange={e => setNewLang(p => ({ ...p, name: e.target.value }))} required />
            </div>
            <div style={{ flex: "0 0 80px" }}>
              <label style={lbl}>Drapeau</label>
              <input style={inp} placeholder="🇯🇵" value={newLang.flag} onChange={e => setNewLang(p => ({ ...p, flag: e.target.value }))} />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
              <button type="submit" style={btn("#16a34a")}>Ajouter</button>
              <button type="button" onClick={() => setShowAdd(false)} style={outBtn}>Annuler</button>
            </div>
          </form>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {languages.map(l => (
            <div key={l.code} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "8px 14px",
              background: l.is_active ? "#f9fafb" : "#ffffff",
              borderRadius: 8, border: `1px solid ${l.is_default ? "#3b82f6" : "#e5e7eb"}`,
              opacity: l.is_active ? 1 : 0.5, minWidth: 160,
            }}>
              <span style={{ fontSize: 18 }}>{l.flag}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: "#111827", fontSize: 13 }}>{l.name}</div>
                <div style={{ fontSize: 11, color: "#4b5563" }}>{l.code.toUpperCase()}</div>
              </div>
              {l.is_default && <Badge color="#3b82f6">défaut</Badge>}
              <div style={{ display: "flex", gap: 4 }}>
                {!l.is_default && <button onClick={() => setDefault(l)} title="Définir par défaut" style={{ ...outBtn, padding: "3px 8px", fontSize: 11 }}>⭐</button>}
                <button onClick={() => toggleActive(l)} style={{ ...outBtn, padding: "3px 8px", fontSize: 11 }}>
                  {l.is_active ? "Désactiver" : "Activer"}
                </button>
                {!l.is_default && <button onClick={() => deleteLang(l)} style={{ ...outBtn, padding: "3px 8px", fontSize: 11, color: "#ef4444" }}>🗑️</button>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Translation matrix */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h3 style={{ margin: 0, color: "#111827", fontSize: 15 }}>Traductions</h3>
            <select value={docType} onChange={e => setDocType(e.target.value)}
              style={{ ...inp, width: "auto", padding: "5px 10px" }}>
              {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {autoResult && (
              <button onClick={applyAutoResult} style={btn("#16a34a")}>
                ✅ Appliquer {autoResult.length} traductions
              </button>
            )}
            <button onClick={autoTranslate} disabled={aiLoading} style={btn("#7c3aed")}>
              {aiLoading ? "Traduction…" : "🤖 Traduire auto (IA)"}
            </button>
            <button onClick={saveAll} disabled={saving || pendingCount === 0} style={btn(pendingCount > 0 ? "#1e40af" : "#e5e7eb")}>
              {saving ? "Sauvegarde…" : `Sauvegarder${pendingCount > 0 ? ` (${pendingCount})` : ""}`}
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Chargement…</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
              <thead>
                <tr style={{ background: "#ffffff" }}>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 12, color: "#4b5563", fontWeight: 600, width: 180, whiteSpace: "nowrap" }}>Champ</th>
                  {activeLangs.map(l => (
                    <th key={l.code} style={{ padding: "8px 8px", textAlign: "left", fontSize: 12, color: "#4b5563", fontWeight: 600 }}>
                      {l.flag} {l.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fieldKeys.map(fk => (
                  <tr key={fk.key} style={{ borderBottom: "1px solid #1e293b" }}>
                    <td style={{ padding: "6px 10px", fontSize: 12, color: "#4b5563", whiteSpace: "nowrap" }}>{fk.label}</td>
                    {activeLangs.map(l => {
                      const val = translations[fk.key]?.[l.code] || "";
                      const isDirty = pendingTr[`${fk.key}__${l.code}`] !== undefined;
                      return (
                        <td key={l.code} style={{ padding: "4px 6px" }}>
                          <textarea
                            value={val}
                            onChange={e => updateCell(fk.key, l.code, e.target.value)}
                            rows={val.length > 60 ? 2 : 1}
                            style={{
                              ...inp, fontSize: 12, padding: "5px 7px", resize: "vertical",
                              borderColor: isDirty ? "#f59e0b" : "#f9fafb",
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DEVISES TAB
// ─────────────────────────────────────────────────────────────────
function DevisesTab() {
  const { showToast } = useToast();
  const [currencies, setCurrencies] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [editing, setEditing]       = useState(null);
  const [showAdd, setShowAdd]       = useState(false);
  const [form, setForm]             = useState({ code: "", name: "", symbol: "", exchange_rate: "1.0" });

  const load = () => api.multilingues.currencies().then(setCurrencies);
  useEffect(() => { load(); }, []);

  async function refresh() {
    setRefreshing(true);
    try {
      const r = await api.multilingues.refreshRates();
      showToast(`${r.updated} devises mises à jour.`, "success");
      load();
    } catch (err) { showToast(err.message, "error"); }
    finally { setRefreshing(false); }
  }

  async function toggleActive(cur) {
    try { await api.multilingues.updateCurrency(cur.code, { is_active: !cur.is_active }); load(); }
    catch (err) { showToast(err.message, "error"); }
  }

  async function saveEdit(cur) {
    try {
      await api.multilingues.updateCurrency(cur.code, { exchange_rate: parseFloat(editing.rate) });
      setEditing(null); load(); showToast("Taux mis à jour.", "success");
    } catch (err) { showToast(err.message, "error"); }
  }

  async function addCurrency(e) {
    e.preventDefault();
    try {
      await api.multilingues.createCurrency(form);
      load(); setShowAdd(false); setForm({ code: "", name: "", symbol: "", exchange_rate: "1.0" });
      showToast("Devise ajoutée.", "success");
    } catch (err) { showToast(err.message, "error"); }
  }

  async function deleteCur(cur) {
    if (!window.confirm(`Supprimer ${cur.code} ?`)) return;
    try { await api.multilingues.deleteCurrency(cur.code); load(); showToast("Devise supprimée.", "success"); }
    catch (err) { showToast(err.message, "error"); }
  }

  const staleAlert = currencies.some(c => {
    if (!c.last_rate_update) return true;
    const diff = Date.now() - new Date(c.last_rate_update).getTime();
    return diff > 26 * 3600 * 1000;
  });

  return (
    <div>
      {staleAlert && (
        <div style={{ background: "#78350f22", border: "1px solid #f59e0b", borderRadius: 8, padding: "10px 16px", marginBottom: 14, color: "#f59e0b", fontSize: 13 }}>
          ⚠️ Certains taux de change n'ont pas été mis à jour depuis plus de 24h. Utilisez le bouton "Actualiser" pour obtenir les derniers taux.
        </div>
      )}

      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: "#111827", fontSize: 15 }}>Devises & Taux de change</h3>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setShowAdd(!showAdd)} style={outBtn}>+ Ajouter</button>
            <button onClick={refresh} disabled={refreshing} style={btn("#16a34a")}>
              {refreshing ? "Actualisation…" : "🔄 Actualiser les taux (Frankfurter)"}
            </button>
          </div>
        </div>

        {showAdd && (
          <form onSubmit={addCurrency} style={{ display: "flex", gap: 10, marginBottom: 14, background: "#ffffff", padding: 12, borderRadius: 8 }}>
            <div><label style={lbl}>Code *</label><input style={{ ...inp, width: 70 }} placeholder="USD" maxLength={5} value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} required /></div>
            <div style={{ flex: 1 }}><label style={lbl}>Nom *</label><input style={inp} placeholder="Dollar américain" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required /></div>
            <div><label style={lbl}>Symbole *</label><input style={{ ...inp, width: 60 }} placeholder="$" value={form.symbol} onChange={e => setForm(p => ({ ...p, symbol: e.target.value }))} required /></div>
            <div><label style={lbl}>Taux / EUR</label><input style={{ ...inp, width: 90 }} type="number" step="0.0001" value={form.exchange_rate} onChange={e => setForm(p => ({ ...p, exchange_rate: e.target.value }))} /></div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
              <button type="submit" style={btn("#16a34a")}>Ajouter</button>
              <button type="button" onClick={() => setShowAdd(false)} style={outBtn}>×</button>
            </div>
          </form>
        )}

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#ffffff", borderBottom: "1px solid #21262d" }}>
              {["Devise", "Nom", "Symbole", "Taux / EUR", "Mis à jour", "Statut", "Actions"].map(h => (
                <th key={h} style={{ padding: "9px 12px", textAlign: "left", fontSize: 12, color: "#4b5563", fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currencies.map(c => {
              const stale = c.last_rate_update && (Date.now() - new Date(c.last_rate_update).getTime() > 26 * 3600 * 1000);
              return (
                <tr key={c.code} style={{ borderBottom: "1px solid #1e293b" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#ffffff"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td style={{ padding: "9px 12px", fontWeight: 700, color: "#38bdf8", fontSize: 14 }}>{c.code}</td>
                  <td style={{ padding: "9px 12px", fontSize: 13 }}>{c.name}</td>
                  <td style={{ padding: "9px 12px", fontSize: 14, fontWeight: 600 }}>{c.symbol}</td>
                  <td style={{ padding: "9px 12px" }}>
                    {editing?.code === c.code ? (
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <input type="number" step="0.0001" value={editing.rate}
                          onChange={e => setEditing(p => ({ ...p, rate: e.target.value }))}
                          style={{ ...inp, width: 100 }} autoFocus />
                        <button onClick={() => saveEdit(c)} style={btn("#16a34a")}>✓</button>
                        <button onClick={() => setEditing(null)} style={outBtn}>×</button>
                      </div>
                    ) : (
                      <span style={{ fontSize: 13, cursor: "pointer", color: stale ? "#f59e0b" : "#111827" }}
                        onClick={() => setEditing({ code: c.code, rate: c.exchange_rate })}>
                        {c.exchange_rate?.toFixed(4)} {stale && "⚠️"}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "9px 12px", fontSize: 11, color: "#4b5563" }}>
                    {c.last_rate_update ? new Date(c.last_rate_update).toLocaleString('fr-FR') : "Jamais"}
                  </td>
                  <td style={{ padding: "9px 12px" }}>
                    <Badge color={c.is_active ? "#16a34a" : "#4b5563"}>{c.is_active ? "Active" : "Inactive"}</Badge>
                  </td>
                  <td style={{ padding: "9px 12px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => toggleActive(c)} style={{ ...outBtn, fontSize: 11, padding: "3px 8px" }}>
                        {c.is_active ? "Désactiver" : "Activer"}
                      </button>
                      {c.code !== "EUR" && (
                        <button onClick={() => deleteCur(c)} style={{ ...outBtn, fontSize: 11, padding: "3px 8px", color: "#ef4444" }}>🗑️</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ marginTop: 14, padding: "10px 14px", background: "#ffffff", borderRadius: 8, fontSize: 12, color: "#4b5563" }}>
          <strong style={{ color: "#4b5563" }}>Source :</strong> api.frankfurter.app — Taux de change en temps réel basés sur la BCE.
          Mis à jour automatiquement chaque jour à 07h00. Cliquez <em>Actualiser</em> pour forcer la mise à jour.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MENTIONS LÉGALES TAB
// ─────────────────────────────────────────────────────────────────
function MentionsTab() {
  const { showToast } = useToast();
  const [mentions, setMentions] = useState([]);
  const [filter, setFilter]     = useState({ country: "all", type: "all" });
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState({
    country_code: "", country_name: "", document_type: "facture",
    mention_type: "tva", content: "", language_code: "fr", is_mandatory: 1,
  });

  const load = () => api.multilingues.legalMentions().then(setMentions);
  useEffect(() => { load(); }, []);

  const countries = [...new Set(mentions.map(m => m.country_code))].sort();
  const types = [...new Set(mentions.map(m => m.mention_type))].sort();

  const filtered = mentions.filter(m =>
    (filter.country === "all" || m.country_code === filter.country) &&
    (filter.type === "all" || m.mention_type === filter.type)
  );

  function openModal(m = null) {
    if (m) setForm({ ...m });
    else setForm({ country_code: "", country_name: "", document_type: "facture", mention_type: "tva", content: "", language_code: "fr", is_mandatory: 1 });
    setModal(m ? "edit" : "create");
  }

  async function save(e) {
    e.preventDefault();
    try {
      if (modal === "edit") await api.multilingues.updateMention(form.id, form);
      else await api.multilingues.createMention(form);
      load(); setModal(null);
      showToast("Mention enregistrée.", "success");
    } catch (err) { showToast(err.message, "error"); }
  }

  async function del(m) {
    if (!window.confirm(`Supprimer cette mention (${m.country_code} — ${m.mention_type}) ?`)) return;
    try { await api.multilingues.deleteMention(m.id); load(); showToast("Supprimée.", "success"); }
    catch (err) { showToast(err.message, "error"); }
  }

  const typeLabel = t => MENTION_TYPES.find(x => x.value === t)?.label || t;
  const typeColor = { tva: "#3b82f6", pénalités_retard: "#ef4444", escompte: "#16a34a", tribunal_compétent: "#8b5cf6", rgpd: "#f59e0b", autre: "#4b5563" };

  return (
    <div>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
          <h3 style={{ margin: 0, color: "#111827", fontSize: 15 }}>Mentions légales par pays</h3>
          <button onClick={() => openModal()} style={btn()}>+ Ajouter une mention</button>
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <select value={filter.country} onChange={e => setFilter(p => ({ ...p, country: e.target.value }))}
            style={{ ...inp, width: "auto" }}>
            <option value="all">Tous les pays</option>
            {countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filter.type} onChange={e => setFilter(p => ({ ...p, type: e.target.value }))}
            style={{ ...inp, width: "auto" }}>
            <option value="all">Tous les types</option>
            {MENTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Aucune mention pour cette sélection.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filtered.map(m => (
              <div key={m.id} style={{ background: "#ffffff", borderRadius: 8, padding: "12px 16px", border: "1px solid #1e293b", display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#38bdf8" }}>{m.country_code}</span>
                    <span style={{ fontSize: 12, color: "#4b5563" }}>{m.country_name}</span>
                    <Badge color={typeColor[m.mention_type] || "#4b5563"}>{typeLabel(m.mention_type)}</Badge>
                    <Badge color={m.is_mandatory ? "#ef4444" : "#4b5563"}>{m.is_mandatory ? "Obligatoire" : "Optionnel"}</Badge>
                    <Badge color="#e5e7eb">{m.document_type}</Badge>
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: "#4b5563", lineHeight: 1.5 }}>{m.content}</p>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button onClick={() => openModal(m)} style={{ ...outBtn, padding: "4px 10px", fontSize: 11 }}>✏️</button>
                  <button onClick={() => del(m)} style={{ ...outBtn, padding: "4px 10px", fontSize: 11, color: "#ef4444" }}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <form onSubmit={save} style={{ background: "#ffffff", border: "1px solid #21262d", borderRadius: 12, width: "100%", maxWidth: 600, padding: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
              <h3 style={{ margin: 0, color: "#111827" }}>{modal === "edit" ? "Modifier" : "Ajouter"} une mention légale</h3>
              <button type="button" onClick={() => setModal(null)} style={{ background: "none", border: "none", color: "#4b5563", fontSize: 20, cursor: "pointer" }}>×</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12, marginBottom: 12 }}>
              <div><label style={lbl}>Code pays *</label><input style={inp} placeholder="FR" value={form.country_code} onChange={e => setForm(p => ({ ...p, country_code: e.target.value.toUpperCase() }))} required /></div>
              <div><label style={lbl}>Nom du pays *</label><input style={inp} placeholder="France" value={form.country_name} onChange={e => setForm(p => ({ ...p, country_name: e.target.value }))} required /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={lbl}>Type de document</label>
                <select style={inp} value={form.document_type} onChange={e => setForm(p => ({ ...p, document_type: e.target.value }))}>
                  {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Type de mention</label>
                <select style={inp} value={form.mention_type} onChange={e => setForm(p => ({ ...p, mention_type: e.target.value }))}>
                  {MENTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Langue</label>
                <input style={inp} placeholder="fr" value={form.language_code} onChange={e => setForm(p => ({ ...p, language_code: e.target.value }))} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Contenu *</label>
              <textarea style={{ ...inp, height: 100, resize: "vertical" }} value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} required />
            </div>
            <div style={{ marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" id="mandatory" checked={!!form.is_mandatory} onChange={e => setForm(p => ({ ...p, is_mandatory: e.target.checked ? 1 : 0 }))} />
              <label htmlFor="mandatory" style={{ color: "#4b5563", fontSize: 13 }}>Mention obligatoire (affichée par défaut sur chaque document)</label>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" onClick={() => setModal(null)} style={outBtn}>Annuler</button>
              <button type="submit" style={btn()}>Enregistrer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DOCUMENTS EXPORT TAB
// ─────────────────────────────────────────────────────────────────
function ExportTab({ companyId }) {
  const { showToast } = useToast();
  const [activeDoc, setActiveDoc] = useState("eur1");
  const [history, setHistory]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [invoices, setInvoices]   = useState([]);

  useEffect(() => {
    if (companyId) {
      api.multilingues.exportDocs(companyId).then(setHistory).catch(() => {});
      api.invoices.list({ company_id: companyId, doc_type: "facture", limit: 50 })
        .then(d => setInvoices(d.invoices || [])).catch(() => {});
    }
  }, [companyId]);

  function openBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  // ── EUR.1 form ──
  const [eur1, setEur1] = useState({ country_destination: "", exporter_name: "", exporter_address: "", exporter_vat: "", consignee_name: "", consignee_address: "", goods_description: "", hs_code: "", gross_weight: "", net_weight: "", origin_country: "FR", packages_count: "", currency: "EUR", value: "", incoterm: "FOB", invoice_id: "" });

  async function generateEur1(e) {
    e.preventDefault(); setLoading(true);
    try {
      const blob = await api.multilingues.generateEur1({ ...eur1, company_id: companyId });
      openBlob(blob, `EUR1-${Date.now()}.pdf`);
      showToast("EUR.1 généré.", "success");
    } catch (err) { showToast(err.message, "error"); }
    finally { setLoading(false); }
  }

  // ── Packing List form ──
  const [pl, setPl]     = useState({ country_destination: "", exporter_name: "", exporter_address: "", consignee_name: "", consignee_address: "", reference: "", incoterm: "DAP", transport_mode: "", vessel_flight: "", port_origin: "", port_dest: "", invoice_id: "", lines: [] });
  const [plLines, setPlLines] = useState([{ ref: "", designation: "", qty: "", unit: "pièce", gross_weight: "", net_weight: "", dimensions: "", hs_code: "" }]);

  async function generatePL(e) {
    e.preventDefault(); setLoading(true);
    try {
      const blob = await api.multilingues.generatePackingList({ ...pl, company_id: companyId, lines: plLines });
      openBlob(blob, `PackingList-${Date.now()}.pdf`);
      showToast("Packing List générée.", "success");
    } catch (err) { showToast(err.message, "error"); }
    finally { setLoading(false); }
  }

  // ── Customs Invoice form ──
  const [ci, setCi] = useState({ country_destination: "", exporter_name: "", exporter_address: "", exporter_vat: "", consignee_name: "", consignee_address: "", incoterm: "DAP", currency: "EUR", statistical_value: "", port_origin: "", port_dest: "", hs_code: "", transport_mode: "sea", invoice_id: "" });

  async function generateCI(e) {
    e.preventDefault(); setLoading(true);
    try {
      const blob = await api.multilingues.generateCustomsInv({ ...ci, company_id: companyId });
      openBlob(blob, `CustomsInvoice-${Date.now()}.pdf`);
      showToast("Facture douanière générée.", "success");
    } catch (err) { showToast(err.message, "error"); }
    finally { setLoading(false); }
  }

  // ── DEB XML form ──
  const [deb, setDeb] = useState({ period_month: new Date().getMonth() + 1, period_year: new Date().getFullYear(), declarant_siret: "", declarant_vat: "", declarant_name: "", declarant_address: "", flow_type: "expedition", lines: [] });
  const [debLines, setDebLines] = useState([{ hs_code: "", country_code: "DE", origin_country: "FR", net_weight: "", qty: "", value: "", transaction_code: "1", transport_mode: "3", region_code: "11" }]);

  async function generateDEB(e) {
    e.preventDefault(); setLoading(true);
    try {
      const blob = await api.multilingues.generateDeb({ ...deb, company_id: companyId, lines: debLines });
      openBlob(blob, `DEB-${deb.period_year}${String(deb.period_month).padStart(2,'0')}.xml`);
      showToast("DEB XML généré.", "success");
    } catch (err) { showToast(err.message, "error"); }
    finally { setLoading(false); }
  }

  const docTabs = [
    { id: "eur1",     label: "Certificat EUR.1", icon: "📜" },
    { id: "packing",  label: "Packing List",      icon: "📋" },
    { id: "customs",  label: "Facture douanière", icon: "🛃" },
    { id: "deb",      label: "DEB / Intrastat",   icon: "🇪🇺" },
  ];

  const fieldRow = (label, children) => (
    <div style={{ marginBottom: 10 }}>
      <label style={lbl}>{label}</label>
      {children}
    </div>
  );

  return (
    <div>
      {/* Sub-tab nav */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {docTabs.map(t => (
          <button key={t.id} onClick={() => setActiveDoc(t.id)}
            style={{ ...outBtn, borderColor: activeDoc === t.id ? "#3b82f6" : "#e5e7eb", color: activeDoc === t.id ? "#111827" : "#4b5563", fontWeight: activeDoc === t.id ? 600 : 400 }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* EUR.1 */}
      {activeDoc === "eur1" && (
        <div style={card}>
          <h3 style={{ margin: "0 0 14px", color: "#111827" }}>Certificat de circulation EUR.1</h3>
          <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 16 }}>Prouve l'origine préférentielle des marchandises pour bénéficier de droits réduits dans les pays avec accord commercial UE.</div>
          <form onSubmit={generateEur1}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <h4 style={{ color: "#4b5563", fontSize: 13, marginBottom: 10 }}>Exportateur</h4>
                {fieldRow("Nom", <input style={inp} value={eur1.exporter_name} onChange={e => setEur1(p => ({ ...p, exporter_name: e.target.value }))} />)}
                {fieldRow("Adresse", <input style={inp} value={eur1.exporter_address} onChange={e => setEur1(p => ({ ...p, exporter_address: e.target.value }))} />)}
                {fieldRow("N° TVA", <input style={inp} value={eur1.exporter_vat} onChange={e => setEur1(p => ({ ...p, exporter_vat: e.target.value }))} />)}
              </div>
              <div>
                <h4 style={{ color: "#4b5563", fontSize: 13, marginBottom: 10 }}>Destinataire</h4>
                {fieldRow("Nom", <input style={inp} value={eur1.consignee_name} onChange={e => setEur1(p => ({ ...p, consignee_name: e.target.value }))} />)}
                {fieldRow("Adresse", <input style={inp} value={eur1.consignee_address} onChange={e => setEur1(p => ({ ...p, consignee_address: e.target.value }))} />)}
                {fieldRow("Pays destination *", <input style={inp} placeholder="US, MA, GB..." value={eur1.country_destination} onChange={e => setEur1(p => ({ ...p, country_destination: e.target.value.toUpperCase() }))} required />)}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 10, marginTop: 10 }}>
              {fieldRow("Description marchandises *", <input style={inp} value={eur1.goods_description} onChange={e => setEur1(p => ({ ...p, goods_description: e.target.value }))} required />)}
              {fieldRow("Code SH (HS)", <input style={inp} placeholder="0901.21" value={eur1.hs_code} onChange={e => setEur1(p => ({ ...p, hs_code: e.target.value }))} />)}
              {fieldRow("Poids brut (kg)", <input style={inp} type="number" value={eur1.gross_weight} onChange={e => setEur1(p => ({ ...p, gross_weight: e.target.value }))} />)}
              {fieldRow("Poids net (kg)", <input style={inp} type="number" value={eur1.net_weight} onChange={e => setEur1(p => ({ ...p, net_weight: e.target.value }))} />)}
              {fieldRow("Valeur (€)", <input style={inp} type="number" value={eur1.value} onChange={e => setEur1(p => ({ ...p, value: e.target.value }))} />)}
            </div>
            {invoices.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <label style={lbl}>Lier à une facture (optionnel)</label>
                <select style={inp} value={eur1.invoice_id} onChange={e => setEur1(p => ({ ...p, invoice_id: e.target.value }))}>
                  <option value="">— Aucune —</option>
                  {invoices.map(i => <option key={i.id} value={i.id}>{i.number} — {i.client_name}</option>)}
                </select>
              </div>
            )}
            <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
              <button type="submit" disabled={loading} style={btn("#1e40af")}>
                {loading ? "Génération…" : "📄 Générer EUR.1 (PDF)"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Packing List */}
      {activeDoc === "packing" && (
        <div style={card}>
          <h3 style={{ margin: "0 0 14px", color: "#111827" }}>Packing List</h3>
          <form onSubmit={generatePL}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 12 }}>
              <div>
                {fieldRow("Expéditeur", <input style={inp} value={pl.exporter_name} onChange={e => setPl(p => ({ ...p, exporter_name: e.target.value }))} />)}
                {fieldRow("Adresse expéditeur", <input style={inp} value={pl.exporter_address} onChange={e => setPl(p => ({ ...p, exporter_address: e.target.value }))} />)}
                {fieldRow("Référence", <input style={inp} value={pl.reference} onChange={e => setPl(p => ({ ...p, reference: e.target.value }))} />)}
              </div>
              <div>
                {fieldRow("Destinataire", <input style={inp} value={pl.consignee_name} onChange={e => setPl(p => ({ ...p, consignee_name: e.target.value }))} />)}
                {fieldRow("Adresse destinataire", <input style={inp} value={pl.consignee_address} onChange={e => setPl(p => ({ ...p, consignee_address: e.target.value }))} />)}
                {fieldRow("Pays destination", <input style={inp} value={pl.country_destination} onChange={e => setPl(p => ({ ...p, country_destination: e.target.value.toUpperCase() }))} />)}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
              {fieldRow("Incoterm", <select style={inp} value={pl.incoterm} onChange={e => setPl(p => ({ ...p, incoterm: e.target.value }))}>
                {INCOTERMS.map(i => <option key={i}>{i}</option>)}</select>)}
              {fieldRow("Mode transport", <input style={inp} placeholder="Sea / Air / Road" value={pl.transport_mode} onChange={e => setPl(p => ({ ...p, transport_mode: e.target.value }))} />)}
              {fieldRow("Navire / Vol", <input style={inp} value={pl.vessel_flight} onChange={e => setPl(p => ({ ...p, vessel_flight: e.target.value }))} />)}
              {fieldRow("Port destination", <input style={inp} value={pl.port_dest} onChange={e => setPl(p => ({ ...p, port_dest: e.target.value }))} />)}
            </div>

            <h4 style={{ color: "#4b5563", fontSize: 13, margin: "0 0 8px" }}>Articles</h4>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10 }}>
              <thead>
                <tr style={{ background: "#ffffff" }}>
                  {["Réf.", "Désignation", "Qté", "Unité", "P. brut (kg)", "P. net (kg)", "Dimensions", "Code SH", ""].map((h, i) => (
                    <th key={i} style={{ padding: "6px 6px", fontSize: 11, color: "#4b5563", fontWeight: 600, textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {plLines.map((l, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #1e293b" }}>
                    {["ref","designation","qty","unit","gross_weight","net_weight","dimensions","hs_code"].map(f => (
                      <td key={f} style={{ padding: "3px 4px" }}>
                        <input style={{ ...inp, fontSize: 12, padding: "4px 6px" }} value={l[f] || ""} onChange={e => setPlLines(ls => ls.map((x, idx) => idx === i ? { ...x, [f]: e.target.value } : x))} />
                      </td>
                    ))}
                    <td style={{ padding: "3px 4px" }}>
                      <button type="button" onClick={() => setPlLines(ls => ls.filter((_, idx) => idx !== i))} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer" }}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" onClick={() => setPlLines(ls => [...ls, { ref:"",designation:"",qty:"",unit:"pièce",gross_weight:"",net_weight:"",dimensions:"",hs_code:"" }])}
              style={{ ...outBtn, fontSize: 12, marginBottom: 12 }}>+ Article</button>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="submit" disabled={loading} style={btn("#1e40af")}>{loading ? "Génération…" : "📋 Générer Packing List (PDF)"}</button>
            </div>
          </form>
        </div>
      )}

      {/* Customs Invoice */}
      {activeDoc === "customs" && (
        <div style={card}>
          <h3 style={{ margin: "0 0 14px", color: "#111827" }}>Facture douanière (Customs Invoice)</h3>
          <form onSubmit={generateCI}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 12 }}>
              <div>
                {fieldRow("Exportateur", <input style={inp} value={ci.exporter_name} onChange={e => setCi(p => ({ ...p, exporter_name: e.target.value }))} />)}
                {fieldRow("Adresse exportateur", <input style={inp} value={ci.exporter_address} onChange={e => setCi(p => ({ ...p, exporter_address: e.target.value }))} />)}
                {fieldRow("N° TVA exportateur", <input style={inp} value={ci.exporter_vat} onChange={e => setCi(p => ({ ...p, exporter_vat: e.target.value }))} />)}
              </div>
              <div>
                {fieldRow("Destinataire", <input style={inp} value={ci.consignee_name} onChange={e => setCi(p => ({ ...p, consignee_name: e.target.value }))} />)}
                {fieldRow("Adresse", <input style={inp} value={ci.consignee_address} onChange={e => setCi(p => ({ ...p, consignee_address: e.target.value }))} />)}
                {fieldRow("Pays destination *", <input style={inp} placeholder="US, GB..." value={ci.country_destination} onChange={e => setCi(p => ({ ...p, country_destination: e.target.value.toUpperCase() }))} required />)}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
              {fieldRow("Incoterm", <select style={inp} value={ci.incoterm} onChange={e => setCi(p => ({ ...p, incoterm: e.target.value }))}>{INCOTERMS.map(i => <option key={i}>{i}</option>)}</select>)}
              {fieldRow("Devise", <input style={inp} placeholder="EUR" value={ci.currency} onChange={e => setCi(p => ({ ...p, currency: e.target.value.toUpperCase() }))} />)}
              {fieldRow("Code SH (HS)", <input style={inp} placeholder="0901.21" value={ci.hs_code} onChange={e => setCi(p => ({ ...p, hs_code: e.target.value }))} />)}
              {fieldRow("Port destination", <input style={inp} value={ci.port_dest} onChange={e => setCi(p => ({ ...p, port_dest: e.target.value }))} />)}
              {fieldRow("Valeur statistique", <input style={inp} type="number" value={ci.statistical_value} onChange={e => setCi(p => ({ ...p, statistical_value: e.target.value }))} />)}
            </div>
            {invoices.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Importer les lignes depuis une facture</label>
                <select style={inp} value={ci.invoice_id} onChange={e => setCi(p => ({ ...p, invoice_id: e.target.value }))}>
                  <option value="">— Sélectionner une facture —</option>
                  {invoices.map(i => <option key={i.id} value={i.id}>{i.number} — {i.client_name} ({i.ttc_amount?.toFixed(2)} €)</option>)}
                </select>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="submit" disabled={loading} style={btn("#1e40af")}>{loading ? "Génération…" : "🛃 Générer Facture douanière (PDF)"}</button>
            </div>
          </form>
        </div>
      )}

      {/* DEB / Intrastat */}
      {activeDoc === "deb" && (
        <div style={card}>
          <h3 style={{ margin: "0 0 4px", color: "#111827" }}>DEB / Déclaration d'Échanges de Biens (Intrastat)</h3>
          <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 14 }}>Génère un fichier XML conforme au format Intrastat / DEB pour les échanges intra-UE. Importable sur douanes.gouv.fr.</div>
          <form onSubmit={generateDEB}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
              {fieldRow("Mois *", <select style={inp} value={deb.period_month} onChange={e => setDeb(p => ({ ...p, period_month: e.target.value }))}>
                {[...Array(12)].map((_, i) => <option key={i+1} value={i+1}>{String(i+1).padStart(2,'0')}</option>)}</select>)}
              {fieldRow("Année *", <input style={inp} type="number" value={deb.period_year} onChange={e => setDeb(p => ({ ...p, period_year: e.target.value }))} required />)}
              {fieldRow("Flux", <select style={inp} value={deb.flow_type} onChange={e => setDeb(p => ({ ...p, flow_type: e.target.value }))}>
                <option value="expedition">Expédition (→)</option>
                <option value="introduction">Introduction (←)</option></select>)}
              {fieldRow("SIRET *", <input style={inp} placeholder="12345678900000" value={deb.declarant_siret} onChange={e => setDeb(p => ({ ...p, declarant_siret: e.target.value }))} required />)}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              {fieldRow("Raison sociale", <input style={inp} value={deb.declarant_name} onChange={e => setDeb(p => ({ ...p, declarant_name: e.target.value }))} />)}
              {fieldRow("N° TVA intracommunautaire", <input style={inp} placeholder="FR12345678901" value={deb.declarant_vat} onChange={e => setDeb(p => ({ ...p, declarant_vat: e.target.value }))} />)}
            </div>

            <h4 style={{ color: "#4b5563", fontSize: 13, margin: "0 0 8px" }}>Lignes de marchandises</h4>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
                <thead>
                  <tr style={{ background: "#ffffff" }}>
                    {["Code SH (8 chiffres)","Pays partenaire","Pays origine","Poids net (kg)","Quantité","Valeur (€)","Nature","Mode transp.","Région"].map((h,i) => (
                      <th key={i} style={{ padding:"5px 6px",fontSize:10,color:"#4b5563",fontWeight:600,textAlign:"left",whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {debLines.map((l, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #1e293b" }}>
                      {[["hs_code","0901210000"],["country_code","DE"],["origin_country","FR"],["net_weight",""],["qty",""],["value",""],["transaction_code","1"],["transport_mode","3"],["region_code","11"]].map(([f, ph]) => (
                        <td key={f} style={{ padding:"3px 3px" }}>
                          <input style={{ ...inp,fontSize:11,padding:"3px 5px" }} placeholder={ph} value={l[f]||""} onChange={e => setDebLines(ls => ls.map((x,idx) => idx===i?{...x,[f]:e.target.value}:x))} />
                        </td>
                      ))}
                      <td><button type="button" onClick={() => setDebLines(ls => ls.filter((_,idx)=>idx!==i))} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer"}}>×</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" onClick={() => setDebLines(ls => [...ls, {hs_code:"",country_code:"DE",origin_country:"FR",net_weight:"",qty:"",value:"",transaction_code:"1",transport_mode:"3",region_code:"11"}])}
              style={{ ...outBtn,fontSize:12,marginTop:8,marginBottom:14 }}>+ Ligne</button>
            <div style={{ display:"flex",justifyContent:"flex-end" }}>
              <button type="submit" disabled={loading} style={btn("#1e40af")}>{loading ? "Génération…" : "🇪🇺 Générer DEB XML (Intrastat)"}</button>
            </div>
          </form>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div style={card}>
          <h4 style={{ margin: "0 0 12px", color: "#4b5563", fontSize: 14 }}>Historique des documents générés</h4>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#ffffff" }}>
                {["Type","Destination","Date","Statut"].map(h => <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: "#4b5563", fontWeight: 600 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {history.map(d => (
                <tr key={d.id} style={{ borderBottom: "1px solid #1e293b" }}>
                  <td style={{ padding: "7px 10px", fontSize: 12, fontWeight: 600, color: "#38bdf8" }}>{d.document_type}</td>
                  <td style={{ padding: "7px 10px", fontSize: 12 }}>{d.country_destination || "—"}</td>
                  <td style={{ padding: "7px 10px", fontSize: 11, color: "#4b5563" }}>{new Date(d.created_at).toLocaleString("fr-FR")}</td>
                  <td style={{ padding: "7px 10px" }}><Badge color="#16a34a">{d.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────
export default function DocumentsMultilingues({ currentCompanyId }) {
  const [activeTab, setActiveTab] = useState("langues");

  const tabContent = () => {
    switch (activeTab) {
      case "langues":  return <LanguesTab />;
      case "devises":  return <DevisesTab />;
      case "mentions": return <MentionsTab />;
      case "export":   return <ExportTab companyId={currentCompanyId} />;
      default: return null;
    }
  };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1300, margin: "0 auto" }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: 0 }}>Documents Multilingues</h1>
        <p style={{ fontSize: 13, color: "#4b5563", margin: "4px 0 0" }}>Traductions, devises, mentions légales et documents douaniers</p>
      </div>

      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "1px solid #21262d" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{
              background: "none", border: "none",
              borderBottom: activeTab === t.id ? "2px solid #3b82f6" : "2px solid transparent",
              color: activeTab === t.id ? "#111827" : "#4b5563",
              padding: "10px 20px", cursor: "pointer", fontSize: 13,
              fontWeight: activeTab === t.id ? 600 : 400,
              display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap",
            }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tabContent()}
    </div>
  );
}
